import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FREE_ENT = { tier: "free", access: "free", label: "Free" };

const BillingContext = createContext({
  entitlement: FREE_ENT,
  config: null,
  billingReady: false,
  refresh: () => {},
  startCheckout: async () => {},
  openPortal: async () => {},
});

export const BillingProvider = ({ children }) => {
  const { user, authReady } = useAuth();
  const [entitlement, setEntitlement] = useState(FREE_ENT);
  const [config, setConfig] = useState(null);
  const [billingReady, setBillingReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [me, cfg] = await Promise.all([
        api.get("/api/billing/me"),
        api.get("/api/billing/config"),
      ]);
      setEntitlement(me.data.entitlement || FREE_ENT);
      setConfig(cfg.data);
    } catch {
      setEntitlement(FREE_ENT);
    } finally {
      setBillingReady(true);
    }
  }, []);

  useEffect(() => {
    if (authReady && user) refresh();
    else if (authReady && !user) {
      setEntitlement(FREE_ENT);
      setBillingReady(true);
    }
  }, [authReady, user, refresh]);

  // Redirect to Stripe Checkout for the Pro plan.
  const startCheckout = useCallback(async () => {
    const { data } = await api.post("/api/billing/checkout", { origin_url: window.location.origin });
    if (data.url) window.location.href = data.url;
  }, []);

  // Open the Stripe Customer Portal to manage/cancel billing.
  const openPortal = useCallback(async () => {
    const { data } = await api.post("/api/billing/portal", { origin_url: window.location.origin });
    if (data.url) window.location.href = data.url;
  }, []);

  return (
    <BillingContext.Provider value={{ entitlement, config, billingReady, refresh, startCheckout, openPortal }}>
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => useContext(BillingContext);
